import { useCallback, useState, useEffect } from 'react';
import { createEngagementClient } from '../../../../src/services/client';
import GET_WORKOUT_NEW from '../engagementGql/getWorkoutNew';
import { addErrorInDatadogRum } from '../utils/utilities';

export type WorkoutTrack = {
  presenters: string[];
  name: string;
  duration: string;
  durationInSeconds: number;
  startTime: number;
  keyMovements: string[];
  music: {
    musicCoverArtist: string;
    musicCoverType: string;
    musicOriginalArtist: string;
    musicTitle: string;
  };
  trackNumber: string;
};

export type WorkoutAsset = {
  videoId: string;
  workoutId?: string;
  name: string;
  product: string;
  provider: string;
  streamUrl: string;
  metaData: any;
  workoutName: string;
};

export type WorkoutData = {
  programName: string;
  workoutName: string;
  tiers: string[];
  imageWide: string;
  equipment: string[];
  categories: string[];
  fitnessLevel: string[];
  durationInSeconds: number;
  name: string;
  id: string;
  vimeoId: string;
  tracks: WorkoutTrack[];
  assets: WorkoutAsset[];
  embedUrl: string;
  description: string | null;
};

type UseGetWorkoutResult = {
  data: WorkoutData | null;
  loading: boolean;
  error: string | null;
};

const useGetWorkout = (workoutId: string | null): UseGetWorkoutResult => {
  const [data, setData] = useState<WorkoutData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkout = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data: workoutData } = await createEngagementClient.query({
        query: GET_WORKOUT_NEW,
        variables: { workoutId: id },
        fetchPolicy: 'cache-first',
      });

      if (workoutData?.workout) {
        setData(workoutData.workout);
      } else {
        setError('Workout not found');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred while fetching workout';
      setError(errorMessage);
      addErrorInDatadogRum(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (workoutId) {
      fetchWorkout(workoutId);
    } else {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, [workoutId, fetchWorkout]);

  return {
    data,
    loading,
    error,
  };
};

export default useGetWorkout;
